import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Loading, StatCard } from "@/components/PageUI";
import { useAuth } from "@/context/AuthContext";
import { Wallet, Lock, TrendingDown, CircleDollarSign } from "lucide-react";
import { creditsText } from "@/lib/studio";
import { toast } from "sonner";
import { SpendLimitsEditor } from "@/components/creative/SpendLimitsEditor";
import { SpendChart } from "@/components/creative/SpendChart";

export default function CreditsUsage() {
  const { user } = useAuth();
  const { data: usage, isLoading, refetch } = useQuery({
    queryKey: ["studio-usage"],
    queryFn: () => api.get("/creative/usage").then(r => r.data),
    retry: false,
  });
  if (isLoading) return <Loading />;
  if (!usage) return <p role="alert" data-testid="credits-load-error">Credits could not be loaded. Please try again.</p>;
  const grant = async () => {
    try { const { data } = await api.post("/admin/creative/credits/grant", { owner_id: user.id, reason: "credits page grant" }); toast.success(`Granted $${data.granted_usd}`); refetch(); }
    catch { toast.error("Grant failed"); }
  };

  return <>
    <PageHeader eyebrow="AI Creative Studio" title="Credits & usage" description={user?.role === "admin" ? "Spend is protected by atomic credit holds and hard daily caps you control below." : "Your Studio credits pay for every AI image, video and voice generation. 1 credit = $0.10."} action={
      user?.role === "admin" ? <button className="secondary-btn" onClick={grant} data-testid="credits-grant-button"><CircleDollarSign size={16} />Grant $100 test credits</button>
        : <Link className="primary-btn" to="/plans#credit-packs" data-testid="credits-buy-button"><CircleDollarSign size={16} />Buy credits</Link>
    } />

    <section className="stats-grid three">
      <StatCard label="Available balance" value={creditsText(usage.balance_usd / usage.credit_usd)} icon={Wallet} tone="green" />
      <StatCard label="Reserved credits" value={creditsText(usage.reserved_usd / usage.credit_usd)} icon={Lock} tone="cyan" />
      <StatCard label="Spent today" value={creditsText(usage.spent_today_usd / usage.credit_usd)} icon={TrendingDown} tone="pink" />
    </section>

    <section className="analytics-grid">
      <article className="panel" data-testid="caps-panel">
        <span className="eyebrow">SPEND CAPS</span><h2>Protection limits</h2>
        <div className="studio-kv">
          <div><small>Per-user daily</small><b data-testid="caps-user-daily">{user?.role === "admin" ? `$${usage.caps.per_user_daily_usd}` : creditsText(usage.caps.per_user_daily_usd / usage.credit_usd)}</b></div>
          {user?.role === "admin" && <div><small>Global daily</small><b>${usage.caps.global_daily_usd}</b></div>}
          <div><small>Max per job</small><b data-testid="caps-per-job">{user?.role === "admin" ? `$${usage.caps.max_single_job_usd}` : creditsText(usage.caps.max_single_job_usd / usage.credit_usd)}</b></div>
          {user?.role === "admin" && <div><small>Kill switch</small><b>{usage.kill_switch ? "ON" : "off"}</b></div>}
          {user?.role === "admin" && <div><small>Markup on provider cost</small><b data-testid="caps-markup">{usage.markup_pct}%</b></div>}
          <div><small>1 credit</small><b data-testid="caps-credit-value">${usage.credit_usd.toFixed(2)}</b></div>
        </div>
      </article>

      <article className="panel span-2" data-testid="estimator-panel">
        <span className="eyebrow">PRICING BEFORE PRODUCTION</span><h2>Review the price in your project</h2>
        <p className="studio-muted">Each supported model is priced for your actual references, duration and sound. The review shows every paid stage and the maximum authorized charge before you generate.</p>
        <p className="studio-muted">Completed, retained stages are itemized in project history. Unused reserved credits are released. An interrupted request is checked, never automatically submitted again.</p>
        <Link className="primary-btn" to="/studio" data-testid="credits-open-studio">Open Creative Studio</Link>
      </article>
      {user?.role === "admin" && <><SpendChart /><SpendLimitsEditor /></>}
    </section>
  </>;
}
